import { Button } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconRefresh, IconAlertTriangle } from "@tabler/icons-react";
import { OwnershipCostPreviewResponse } from "./types";
import { formatNumber } from "@/uiCore";
import { formatCurrencyAmount } from "./ownershipHelpers";

interface OwnershipCostBannerProps {
  costPreview: OwnershipCostPreviewResponse | null;
  isCostPreviewLoading: boolean;
  costPreviewError: string | null;
  hasPreviewableOwnerships: boolean;
  onRefresh: () => void;
  onViewConflicts: () => void;
}

const OwnershipCostBanner = ({
  costPreview,
  isCostPreviewLoading,
  costPreviewError,
  hasPreviewableOwnerships: _hasPreviewableOwnerships,
  onRefresh,
  onViewConflicts,
}: OwnershipCostBannerProps) => {
  const hasConflicts =
    costPreview && costPreview.conflicted_entities_count > 0 && costPreview.conflicted_amount > 0;

  return (
    <div className="al-rounded-lg al-border al-border-blue-100 al-bg-blue-50 al-p-4 al-space-y-3">
      <Stack direction="row" align="start" justify="between" gap="md">
        <div>
          <p className="al-text-sm al-font-medium al-text-foreground">
            Costs attributed to this team (for last 28 days):
          </p>
          <p className="al-text-lg al-text-blue-700 al-mt-1">
            {costPreview ? formatCurrencyAmount(costPreview.amount, costPreview.currency) : "$0"}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="al-gap-2 al-h-8 al-px-2 hover:al-bg-blue-100"
          onClick={onRefresh}
          disabled={isCostPreviewLoading}
        >
          <IconRefresh
            size={16}
            className={
              isCostPreviewLoading ? "al-animate-spin al-text-blue-700" : "al-text-blue-700"
            }
          />
        </Button>
      </Stack>
      <p className="al-text-xs al-text-muted-foreground">
        This is the estimated cost that will be attributed to this team based on the ownership rules defined below,
        excluding entities with conflicts. Click the refresh button to recalculate after modifying rules.
      </p>
      {hasConflicts && (
        <div className="al-rounded-md al-bg-yellow-50 al-border al-border-yellow-200 al-p-3">
          <Stack direction="row" align="start" gap="sm">
            <IconAlertTriangle
              size={16}
              className="al-text-yellow-600 al-mt-0.5 al-flex-shrink-0"
            />
            <div className="al-flex-1">
              <div className="al-text-sm al-text-foreground">
                <span className="al-font-semibold">
                  {formatNumber(costPreview.conflicted_entities_count)}
                </span>{" "}
                entities have unresolved conflicts affecting{" "}
                <span className="al-font-semibold">
                  {formatNumber(costPreview.conflicted_amount, {
                    currency: costPreview.currency,
                  })}
                </span>{" "}
                in costs across multiple teams.{" "}
                <button
                  type="button"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    onViewConflicts();
                  }}
                  className="al-text-blue-600 hover:al-text-blue-800 al-underline al-font-medium al-cursor-pointer al-bg-transparent al-border-none al-p-0"
                >
                  See details
                </button>
              </div>
            </div>
          </Stack>
        </div>
      )}
      {costPreviewError && <p className="al-text-xs al-text-destructive">{costPreviewError}</p>}
    </div>
  );
};

export default OwnershipCostBanner;

